import {FC, useEffect, useMemo, useRef} from 'react'
import classNames from 'classnames'
import {Link, matchPath} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {usePolicy} from 'hooks/usePolicy'
import {useToggle} from 'hooks/useToggle'
import {TENANT_NO_STORAGE_RESIDENCY_REGION} from 'definitions/tenantConfig'
import {useTenantConfigQuery} from 'hooks/useTenantConfigQuery'
import {useAnalyticsTenantDomainGate} from 'features/Analytics/hooks'
import {stringSplitAndJoin} from 'utils'
import {ChevronRightIcon} from '../SvgIcon'
import {TooltipBody, TooltipWrapper} from '../TooltipWrapper'
import {AnalyticsRoutesEnum} from 'interfaces'

const routes = [
  {name: 'Overview', path: AnalyticsRoutesEnum.ANALYTICS_OVERVIEW},
  {name: 'Policy', path: AnalyticsRoutesEnum.ANALYTICS_POLICY},
  {name: 'Productivity', path: AnalyticsRoutesEnum.ANALYTICS_PRODUCTIVITY},
  {name: 'Shadow IT', path: AnalyticsRoutesEnum.ANALYTICS_SHADOW_IT},
  {name: 'AI Usage', path: AnalyticsRoutesEnum.ANALYTICS_AI_USAGE_APPS},
  {name: 'Detail', path: AnalyticsRoutesEnum.ANALYTICS_DETAIL},
]

const ANALYTICS_DROPDOWN_TOOLTIP_ID = 'analytics-route-dropdown-tooltip'
const ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID =
  'analytics-route-dropdown-tenant-tooltip'

const isDropdownItemActive = (
  itemPath: string,
  currentPath: string,
): boolean => {
  if (itemPath === currentPath) {
    return true
  }

  if (itemPath === AnalyticsRoutesEnum.ANALYTICS_AI_USAGE_APPS) {
    return Boolean(
      matchPath(currentPath, {
        path: AnalyticsRoutesEnum.ANALYTICS_AI_USAGE,
        exact: false,
      }),
    )
  }

  return false
}

interface Props {
  bgColor: string
  textColor: string
  text: string
  bgOpacity: string
  bgHoverOpacity: string
  className?: string
  menuClassName?: string
}

export const AnalyticsRouteDropDown: FC<Props> = ({
  bgColor,
  text,
  textColor,
  bgOpacity,
  bgHoverOpacity,
  className,
  menuClassName,
}) => {
  const {t} = useTranslation(['common'])
  const {pathname} = usePolicy()
  const {toggleOn, onToggleClick} = useToggle()
  const ref = useRef<HTMLDivElement>(null)
  const tenantConfigQuery = useTenantConfigQuery()
  const {isAnalyticsDisabledForPrimaryDomain} = useAnalyticsTenantDomainGate()
  const isNoStorageTenantResidency = useMemo(
    () =>
      tenantConfigQuery.isSuccess &&
      tenantConfigQuery.data?.data?.residencyRegion ===
        TENANT_NO_STORAGE_RESIDENCY_REGION,
    [tenantConfigQuery.data, tenantConfigQuery.isSuccess],
  )
  const isAIUsageResidencyAllowed = useMemo(
    () =>
      tenantConfigQuery.isSuccess &&
      tenantConfigQuery.data?.data?.residencyRegion !==
        TENANT_NO_STORAGE_RESIDENCY_REGION,
    [tenantConfigQuery.data, tenantConfigQuery.isSuccess],
  )
  const isTenantRegionalGatePending = useMemo(
    () =>
      !tenantConfigQuery.isSuccess &&
      (tenantConfigQuery.isLoading || tenantConfigQuery.isFetching),
    [
      tenantConfigQuery.isFetching,
      tenantConfigQuery.isLoading,
      tenantConfigQuery.isSuccess,
    ],
  )
  useEffect(() => {
    // to determine clicked target and close the dropdown
    const checkIfClickedOutside = (event: MouseEvent) => {
      if (
        toggleOn &&
        ref.current &&
        !ref.current.contains(event.target as Node)
      ) {
        onToggleClick()
      }
    }

    // adding event listener to know whenever user clicks on page
    document.addEventListener('mousedown', checkIfClickedOutside)

    // cleanup function to remove event listener on unmount
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [toggleOn])

  return (
    <div className="relative" ref={ref}>
      <button
        data-cy="analytics-dropdown"
        onClick={onToggleClick}
        aria-expanded={toggleOn}
        tabIndex={0}
        className={classNames(
          // Combine all the dynamic classes as a single string rather than multiple arguments
          `bg-${bgColor} bg-${bgOpacity} hover:bg-${bgHoverOpacity} border-${bgColor}`,
          // Fixed classes
          'outline-none rounded-full flex items-center justify-center border focus-visible:border-white px-4 py-1 ml-3',
          // Separate argument for the custom className prop
          className,
        )}
      >
        <span className={`text-sm karla-medium ${textColor}`}>{text}</span>
        <ChevronRightIcon
          strokeColor={textColor}
          classes={classNames('transform rotate-90 ml-4', {
            '-rotate-90': toggleOn,
          })}
          width={6}
          height={9}
        />
      </button>

      <div
        className={classNames(
          'absolute z-50 bg-dope-black border border-gray-600 flex flex-col w-40 mt-3 mx-auto',
          menuClassName,
          {
            hidden: toggleOn === false,
          },
        )}
      >
        {routes.map((data, idx) => {
          const isProductivityRoute =
            data.path === AnalyticsRoutesEnum.ANALYTICS_PRODUCTIVITY
          const isShadowItRoute =
            data.path === AnalyticsRoutesEnum.ANALYTICS_SHADOW_IT
          const isAIUsageRoute =
            data.path === AnalyticsRoutesEnum.ANALYTICS_AI_USAGE_APPS
          const isProductivityDisabled =
            isProductivityRoute &&
            (isNoStorageTenantResidency ||
              isTenantRegionalGatePending ||
              isAnalyticsDisabledForPrimaryDomain)
          const isShadowItDisabled =
            isShadowItRoute &&
            (isNoStorageTenantResidency || isTenantRegionalGatePending)
          const isAIUsageDisabled =
            isAIUsageRoute &&
            (!isAIUsageResidencyAllowed || isAnalyticsDisabledForPrimaryDomain)
          const isRouteDisabled =
            isProductivityDisabled || isShadowItDisabled || isAIUsageDisabled

          if (isRouteDisabled) {
            let disabledRouteTooltipProps: Record<string, string> = {}
            if (isNoStorageTenantResidency) {
              disabledRouteTooltipProps = {
                'data-tooltip-content': t(
                  'NO_STORAGE_REGION_RESTRICTED_TOOLTIP',
                ),
                'data-tooltip-id': ANALYTICS_DROPDOWN_TOOLTIP_ID,
              }
            } else if (
              tenantConfigQuery.isSuccess &&
              isAnalyticsDisabledForPrimaryDomain &&
              (isProductivityRoute || isAIUsageRoute)
            ) {
              disabledRouteTooltipProps = {
                'data-tooltip-content': t(
                  'ANALYTICS_TENANT_UNAVAILABLE_TOOLTIP',
                ),
                'data-tooltip-id': ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID,
              }
            }

            return (
              <button
                key={`analytics-route-item-${idx}`}
                data-cy={`analytics-dropdown-option-${stringSplitAndJoin(
                  data?.name?.toLowerCase(),
                )}`}
                {...disabledRouteTooltipProps}
                disabled
                className={classNames('w-full py-2 karla-light text-sm', {
                  'border-b border-gray-700': idx !== routes.length - 1,
                  'cursor-not-allowed text-white/40': true,
                })}
              >
                {data.name}
              </button>
            )
          }

          return (
            <Link
              className={classNames(
                'block w-full py-2 text-center karla-light text-sm hover:bg-gray-800',
                {
                  'border-b border-gray-700': idx !== routes.length - 1,
                  [textColor]: isDropdownItemActive(data.path, pathname),
                },
              )}
              data-cy={`analytics-dropdown-option-${stringSplitAndJoin(
                data?.name?.toLowerCase(),
              )}`}
              to={data.path}
              key={`analytics-route-item-${idx}`}
            >
              {data.name}
            </Link>
          )
        })}
      </div>
      {isNoStorageTenantResidency ? (
        <TooltipWrapper
          id={ANALYTICS_DROPDOWN_TOOLTIP_ID}
          place="bottom"
          className="z-tooltip"
          render={({content}) => (
            <TooltipBody classes="shadow-none" noPadding>
              {content}
            </TooltipBody>
          )}
        />
      ) : null}
      {isAnalyticsDisabledForPrimaryDomain ? (
        <TooltipWrapper
          id={ANALYTICS_DROPDOWN_TENANT_TOOLTIP_ID}
          place="bottom"
          className="z-tooltip"
          render={({content}) => (
            <TooltipBody classes="shadow-none" noPadding>
              {content}
            </TooltipBody>
          )}
        />
      ) : null}
    </div>
  )
}
